import React, { useState, useEffect, useRef, useCallback } from "react";
import { Layout, Table, Input, Button, Form, Space, Row, Col, Breadcrumb, Modal, message, Typography, Select, Tooltip, Tag, Switch, Divider } from "antd";
import Headerdiv from "../../layout/header";
import Footerdiv from "../../layout/footer";
import {
    PlusOutlined, SearchOutlined, CloseCircleFilled, CheckCircleFilled,
    EditOutlined, HomeOutlined,
    ShopOutlined, GlobalOutlined, PhoneOutlined, MailOutlined,
    EnvironmentOutlined, IdcardOutlined, EditTwoTone,
    EyeFilled, DownloadOutlined, FilePdfOutlined, FileExcelOutlined
} from "@ant-design/icons";
import api from "../../../services/axiosInstance";
import dayjs from "dayjs";
import Highlighter from "react-highlight-words";
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import "../../../styles/fluidDesign.css";

const { Content } = Layout;
const { TextArea } = Input;
const { Text } = Typography;

const SUPPLIER_TYPE_OPTIONS = [
    { value: "Individual", label: "Individual" },
    { value: "Company", label: "Company" },
    { value: "Partnership", label: "Partnership" },
    { value: "Other", label: "Other" },

];

const ManageSupplier = () => {
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [currentSupplier, setCurrentSupplier] = useState(null);
    const [viewSupplierData, setViewSupplierData] = useState(null);
    const [addForm] = Form.useForm();
    const [form] = Form.useForm();
    const [isAddModalVisible, setIsAddModalVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });

    const UPDATED_BY = localStorage.getItem("USERID");

    const [searchText, setSearchText] = useState("");
    const [searchedColumn, setSearchedColumn] = useState("");
    const searchInput = useRef(null);

    const handleSearch = (selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters) => {
        clearFilters();
        setSearchText("");
    };

    const getColumnSearchProps = (dataIndex) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
                    style={{ marginBottom: 8, display: "block" }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText(selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button type="link" size="small" onClick={() => close()}>
                        close
                    </Button>
                </Space>
            </div>
        ),

        filterIcon: (filtered) => (
            <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
        ),
        onFilter: (value, record) =>
            record[dataIndex]?.toString().toLowerCase().includes(value.toLowerCase()),
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ""}
                />
            ) : (
                text
            ),
    });

    // Fetch suppliers from the API with pagination
    const fetchSuppliers = useCallback(async (page = 1, pageSize = 10) => {
        try {
            setLoading(true);
            const response = await api.get(`/settings/supplier/listall.php?page=${page}&limit=${pageSize}`);
            const mappedSuppliers = response.data.dataset.map((item) => ({
                supId: item.sup_id,
                supplierType: item.supplier_type,
                supplierName: item.supplier_name,
                supplierRegNo: item.supplier_reg_no,
                supplierAddress: item.supplier_address,
                supplierLandno: item.supplier_landno,
                supplierMobile: item.supplier_mobile,
                supplierEmail: item.supplier_email,
                supplierWeb: item.supplier_web,
                supplierNote: item.supplier_note,
                supplierIsActive: item.supplier_is_active,
                changedBy: item.changed_by,
                changedDt: item.changed_dt,
            }));

            setSuppliers(mappedSuppliers);
            setPagination({
                current: response.data.pagination?.current_page || 1,
                pageSize: response.data.pagination?.per_page || 10,
                total: response.data.pagination?.total_records || 0,
            });
        } catch (error) {
            message.error("Failed to fetch suppliers.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSuppliers(1, 10);
    }, [fetchSuppliers]);

    // Fetch supplier details by ID 
    const fetchSupplierDetailsById = async (id) => {
        try {
            const response = await api.get(`/settings/supplier/detail.php?id=${id}`);
            const item = response.data.dataset;
            setCurrentSupplier(item);
            form.setFieldsValue({
                supId: item.sup_id,
                supplierType: item.supplier_type,
                supplierName: item.supplier_name,
                supplierRegNo: item.supplier_reg_no,
                supplierAddress: item.supplier_address,
                supplierLandno: item.supplier_landno,
                supplierMobile: item.supplier_mobile,
                supplierEmail: item.supplier_email,
                supplierWeb: item.supplier_web,
                supplierNote: item.supplier_note,
                supplierIsActive: item.supplier_is_active === "1",
            });
            setIsModalVisible(true);
        } catch (error) {
            message.error("Failed to fetch supplier details.");
        }
    };

    // Handle edit button click
    const handleEdit = (record) => {
        fetchSupplierDetailsById(record.supId);
    };

    // Fetch supplier details for view only
    const fetchSupplierDetailsForView = async (id) => {
        try {
            setLoading(true);
            const response = await api.get(`/settings/supplier/detail.php?id=${id}`);
            const item = response.data.dataset;
            setViewSupplierData(item);
            setIsViewModalVisible(true);
        } catch (error) {
            message.error("Failed to fetch supplier details.");
        } finally {
            setLoading(false);
        }
    };

    // handle view button click
    const handleView = (record) => {
        fetchSupplierDetailsForView(record.supId);
    };

    // Handle update supplier
    const handleUpdate = async (values) => {
        const payload = {
            sup_id: currentSupplier.sup_id,
            supplier_type: values.supplierType,
            supplier_name: values.supplierName,
            supplier_reg_no: values.supplierRegNo,
            supplier_address: values.supplierAddress,
            supplier_landno: values.supplierLandno,
            supplier_mobile: values.supplierMobile,
            supplier_email: values.supplierEmail,
            supplier_web: values.supplierWeb,
            supplier_note: values.supplierNote,
            supplier_is_active: values.supplierIsActive ? "1" : "0",
            updatedby: UPDATED_BY,
        };
        try {
            setLoading(true);
            const response = await api.post("/settings/supplier/update.php", payload);
            if (response.data.status === "success") {
                message.success(response.data.message || "Supplier updated successfully.");
                setIsModalVisible(false);
                fetchSuppliers(pagination.current, pagination.pageSize);
                form.resetFields();
            } else {
                throw new Error(response.data.message || "Failed to update supplier");
            }
        } catch (error) {
            message.error("Failed to update supplier.");
        } finally {
            setLoading(false);
        }
    };

    // Handle add new supplier
    const handleAdd = async (values) => {
        const payload = {
            supplier_type: values.supplierType,
            supplier_name: values.supplierName,
            supplier_reg_no: values.supplierRegNo,
            supplier_address: values.supplierAddress,
            supplier_landno: values.supplierLandno,
            supplier_mobile: values.supplierMobile,
            supplier_email: values.supplierEmail,
            supplier_web: values.supplierWeb,
            supplier_note: values.supplierNote,
            supplier_is_active: values.supplierIsActive ? "1" : "0",
            createdby: UPDATED_BY,
        };

        try {
            setLoading(true);
            const response = await api.post("/settings/supplier/create.php", payload);
            if (response.data.status === "success") {
                message.success(response.data.message || "Supplier added successfully.");
                fetchSuppliers(pagination.current, pagination.pageSize);
                addForm.resetFields();
            } else {
                throw new Error(response.data.message || "Failed to add supplier");
            }
        } catch (error) {
            message.error("Failed to add supplier. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // Define table columns with search and sort functionality
    const columns = [
        {
            title: "Supplier Name",
            dataIndex: "supplierName",
            key: "supplierName",
            width: 180,
            sorter: (a, b) => (a.supplierName || "").localeCompare(b.supplierName || ""),
            ...getColumnSearchProps("supplierName"),
            render: (text) => (
                <Space>

                    <Text strong>{text}</Text>
                </Space>
            ),
        },
        {
            title: "Type",
            dataIndex: "supplierType",
            key: "supplierType",
            width: 120,
            sorter: (a, b) => (a.supplierType || "").localeCompare(b.supplierType || ""),
            ...getColumnSearchProps("supplierType"),
            render: (type) => (
                <Tag
                    color={type === "Material" ? "blue" : type === "Service" ? "green" : type === "Equipment" ? "purple" : type === "Transport" ? "orange" : type === "Consultancy" ? "cyan" : "default"}
                >
                    {type || "\u2014"}
                </Tag>
            ),
        },
        // {
        //     title: "Reg. No",
        //     dataIndex: "supplierRegNo",
        //     key: "supplierRegNo",
        //     width: 130,
        //     ...getColumnSearchProps("supplierRegNo"),
        //     render: (text) => <Text code style={{ fontSize: 12 }}>{text || "\u2014"}</Text>,
        // },
        {
            title: "Mobile",
            dataIndex: "supplierMobile",
            key: "supplierMobile",
            width: 130,
            ...getColumnSearchProps("supplierMobile"),
            render: (text) =>
                text ? (
                    <Space size={4}>
                        {/* <PhoneOutlined style={{ color: "#52c41a" }} /> */}
                        <Text>{text}</Text>
                    </Space>
                ) : "\u2014",
        },
        {
            title: "Email",
            dataIndex: "supplierEmail",
            key: "supplierEmail",
            width: 180,
            ...getColumnSearchProps("supplierEmail"),
            render: (text) =>
                text ? (
                    <Space size={4}>
                        {/* <MailOutlined style={{ color: "#1677ff" }} /> */}
                        <Text>{text}</Text>
                    </Space>
                ) : "\u2014",
        },
        {
            title: "Landline",
            dataIndex: "supplierLandno",
            key: "supplierLandno",
            width: 120,
            ...getColumnSearchProps("supplierLandno"),
        },
        {
            title: "Website",
            dataIndex: "supplierWeb",
            key: "supplierWeb",
            width: 140,
            ellipsis: true,
            render: (text) =>
                text ? (
                    <Space size={4}>
                        {/* <GlobalOutlined style={{ color: "#722ed1" }} /> */}
                        <a href={text.startsWith("http") ? text : `https://${text}`} target="_blank" rel="noreferrer">
                            {text}
                        </a>
                    </Space>
                ) : "\u2014",
        },
        // {
        //     title: "Address",
        //     dataIndex: "supplierAddress",
        //     key: "supplierAddress",
        //     width: 200,
        //     ellipsis: true,
        //     ...getColumnSearchProps("supplierAddress"),
        //     render: (text) =>
        //         text ? (
        //             <Tooltip title={text}>
        //                 <Space size={4}>
        //                     <EnvironmentOutlined style={{ color: "#fa8c16" }} />
        //                     <Text ellipsis style={{ maxWidth: 160 }}>
        //                         {text}
        //                     </Text>
        //                 </Space>
        //             </Tooltip>
        //         ) : "\u2014",
        // },
        {
            title: "Status",
            dataIndex: "supplierIsActive",
            key: "supplierIsActive",
            align: "center",
            width: 90,
            render: (val) =>
                val == 1 ? (
                    <Tooltip title="Active">
                        <Space size={4}>
                            <CheckCircleFilled style={{ color: "#52c41a", fontSize: 16 }} />
                            <Text style={{ color: "#52c41a" }}>Active</Text>
                        </Space>
                    </Tooltip>
                ) : (
                    <Tooltip title="Inactive">
                        <Space size={4}>
                            <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 16 }} />
                            <Text style={{ color: "#ff4d4f" }}>Inactive</Text>
                        </Space>
                    </Tooltip>
                ),
        },
        // {
        //     title: "Last Updated",
        //     dataIndex: "changedDt",
        //     key: "changedDt",
        //     width: 130,
        //     sorter: (a, b) => (a.changedDt || "").localeCompare(b.changedDt || ""),
        //     render: (date) =>
        //         date ? (
        //             <Text type="secondary" style={{ fontSize: 12 }}>
        //                 {dayjs(date).format("YYYY-MM-DD")}
        //             </Text>
        //         ) : "\u2014",
        // },
        {
            title: "Action",
            key: "action",
            align: "center",
            fixed: "right",
            width: 80,
            render: (_, record) => (
                <>
                    <Tooltip title="View Supplier">
                        <EyeFilled
                            style={{
                                color: "#1890ff",
                                fontSize: 16,
                                marginRight: 12,
                                cursor: "pointer",
                            }}
                            onClick={() => handleView(record)}
                        />
                    </Tooltip>

                    <Tooltip title="Edit Supplier">
                        <Button
                            type="text"
                            icon={
                                <EditOutlined
                                    style={{ color: "var(--fluid-primary)" }}
                                />
                            }
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                </>
            ),
        },
    ];

    // Filter suppliers based on search query and status filter
    const filteredSuppliers = suppliers.filter((supplier) => {
        // Status filter
        if (statusFilter === "active" && supplier.supplierIsActive != 1) return false;
        if (statusFilter === "inactive" && supplier.supplierIsActive == 1) return false;

        // Search filter
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            return (
                (supplier.supplierName && supplier.supplierName.toLowerCase().includes(q)) ||
                (supplier.supplierType && supplier.supplierType.toLowerCase().includes(q)) ||
                (supplier.supplierMobile && supplier.supplierMobile.toLowerCase().includes(q)) ||
                (supplier.supplierEmail && supplier.supplierEmail.toLowerCase().includes(q)) ||
                (supplier.supplierRegNo && supplier.supplierRegNo.toLowerCase().includes(q)) ||
                (supplier.supplierLandno && supplier.supplierLandno.toLowerCase().includes(q)) ||
                (supplier.supplierWeb && supplier.supplierWeb.toLowerCase().includes(q)) ||
                (supplier.supplierAddress && supplier.supplierAddress.toLowerCase().includes(q))
            );
        }

        return true;
    });

    // Export to PDF using jspdf
    const exportToPDF = () => {
        if (filteredSuppliers.length === 0) {
            message.warning("No data to export.");
            return;
        }
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();

        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(45, 90, 61);
        doc.text("Supplier List", 14, 20);

        doc.setFontSize(10);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(100);
        doc.text(`Generated on ${dayjs().format("YYYY-MM-DD HH:mm")}`, 14, 28);
        doc.text(`Records: ${filteredSuppliers.length}`, pageWidth - 14, 28, { align: "right" });

        const tableColumn = ["Supplier Name", "Type", "Mobile", "Email", "Landline", "Website", "Status"];
        const tableRows = filteredSuppliers.map((s) => [
            s.supplierName || "-",
            s.supplierType || "-",
            s.supplierMobile || "-",
            s.supplierEmail || "-",
            s.supplierLandno || "-",
            s.supplierWeb || "-",
            s.supplierIsActive == 1 ? "Active" : "Inactive",
        ]);

        autoTable(doc, {
            head: [tableColumn],
            body: tableRows,
            startY: 35,
            theme: "striped",
            margin: { left: 10, right: 10 },
            headStyles: { fillColor: [45, 90, 61], fontSize: 9, halign: "center" },
            styles: { fontSize: 8, cellPadding: 1.5, overflow: 'linebreak' },
            columnStyles: {
                0: { cellWidth: 52 },      // Supplier Name — longest, give most space
                1: { cellWidth: 18 },      // Type — short
                2: { cellWidth: 28 },      // Mobile — medium
                3: { cellWidth: 38 },      // Email — can be long
                4: { cellWidth: 16 },      // Landline — short
                5: { cellWidth: 'auto' },  // Website — takes remaining space
                6: { cellWidth: 16, halign: 'center' }, // Status — shortest
            },
        });

        doc.save("suppliers.pdf");
        message.success("PDF exported successfully.");
    };

    // Export to Excel using xlsx
    const exportToExcel = () => {
        if (filteredSuppliers.length === 0) {
            message.warning("No data to export.");
            return;
        }
        const data = filteredSuppliers.map((s) => ({
            Name: s.supplierName || "",
            Type: s.supplierType || "",
            Mobile: s.supplierMobile || "",
            Email: s.supplierEmail || "",
            Landline: s.supplierLandno || "",
            Website: s.supplierWeb || "",
            Address: s.supplierAddress || "",
            "Reg. No": s.supplierRegNo || "",
            Status: s.supplierIsActive == 1 ? "Active" : "Inactive",
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Suppliers");
        XLSX.writeFile(wb, "suppliers.xlsx");
        message.success("Excel exported successfully.");
    };

    return (
        <Layout className="fluid-page">
            <Headerdiv />
            {/* // Page header with breadcrumb and title */}
            <div className="fluid-page-header">
                <div className="floating-shape shape-1"></div>
                <div className="floating-shape shape-2"></div>
                <Row align="bottom" justify="space-between">
                    <Col>
                                                    <Breadcrumb
                                className="fluid-breadcrumb"
                                items={[
                                    { href: '/dash', title: <HomeOutlined /> },
                                    { title: 'Settings' },
                                    { title: 'Supplier Management' },
                                ]}
                            />
                        <h1 className="fluid-page-title">
                            <ShopOutlined /> Supplier Management
                        </h1>
                    </Col>
                    <Col xs={0} md={12} style={{ textAlign: "right" }}>
                        <p className="fluid-page-subtitle">
                            Manage suppliers, their contact info and service categories
                        </p>
                    </Col>
                </Row>
            </div>

            {/* // Main content area */}
            <Content className="fluid-content">
                {/* // List of Suppliers Table */}
                <div className="fluid-card">
                    <div className="fluid-card-header">
                        <h3 className="fluid-card-title">
                            <ShopOutlined style={{ marginRight: 8, color: "var(--fluid-primary)" }} />
                            List of Suppliers
                        </h3>
                        <Space>
                            <Text type="secondary">{filteredSuppliers.length} records</Text>
                        </Space>
                    </div>
                    {/* // Toolbar: Search, Filter, Export */}
                    <div style={{ padding: "10px 16px", borderBottom: "1px solid #f0f0f0" }}>
                        <Row gutter={[12, 12]} align="middle" justify="space-between">
                            <Col xs={24} sm={12} md={8} lg={6}>
                                <Input
                                    placeholder="Search suppliers..."
                                    allowClear
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        setPagination((prev) => ({ ...prev, current: 1 }));
                                    }}
                                    prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                                    style={{ width: "100%" }}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={16} lg={18} style={{ textAlign: "right" }}>
                                <Space wrap>
                                    <Button
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={() => setIsAddModalVisible(true)}
                                    >
                                        Add New Supplier
                                    </Button>
                                    <Select allowClear
                                        value={statusFilter}
                                        onChange={(val) => {
                                            setStatusFilter(val);
                                            setPagination((prev) => ({ ...prev, current: 1 }));
                                        }}
                                        style={{ width: 130 }}
                                    >
                                        <Select.Option value="all">All Status</Select.Option>
                                        <Select.Option value="active">Active</Select.Option>
                                        <Select.Option value="inactive">Inactive</Select.Option>
                                    </Select>
                                    <Tooltip title="Export to PDF">
                                        <Button icon={<FilePdfOutlined style={{ color: "#ff4d4f" }} />} onClick={exportToPDF}>
                                            PDF
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Export to Excel">
                                        <Button icon={<FileExcelOutlined style={{ color: "#52c41a" }} />} onClick={exportToExcel}>
                                            Excel
                                        </Button>
                                    </Tooltip>
                                </Space>
                            </Col>
                        </Row>
                    </div>
                    <div className="fluid-table">
                        <Table
                            size="small"
                            columns={columns}
                            dataSource={filteredSuppliers}
                            rowKey="supId"
                            pagination={{
                                current: pagination.current,
                                pageSize: pagination.pageSize,
                                total: filteredSuppliers.length,
                                showSizeChanger: true,
                                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                            }}
                            scroll={{ x: 'max-content' }}
                            loading={loading}
                        />
                    </div>
                </div>
            </Content>
            {/* // Add Supplier Modal */}
            <Modal
                title={
                    <Space>
                        <PlusOutlined style={{ color: "var(--fluid-primary)", fontSize: 20 }} />
                        Add New Supplier
                    </Space>
                }
                open={isAddModalVisible}
                onCancel={() => {
                    setIsAddModalVisible(false);
                    addForm.resetFields();
                }}
                footer={null}
                width={800}
                destroyOnClose
            >
                <Form form={addForm} layout="vertical" onFinish={handleAdd}>
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item
                                label="Supplier Name"
                                name="supplierName"
                                rules={[{ required: true, message: "Please enter supplier name" }]}
                            >
                                <Input
                                    prefix={<ShopOutlined style={{ color: "var(--fluid-primary)" }} />}
                                    placeholder="Enter supplier name"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item
                                label="Supplier Type"
                                name="supplierType"
                                rules={[{ required: true, message: "Please select type" }]}
                            >
                                <Select allowClear placeholder="Select type">
                                    {SUPPLIER_TYPE_OPTIONS.map((opt) => (
                                        <Select.Option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="Reg. No" name="supplierRegNo">
                                <Input prefix={<IdcardOutlined style={{ color: "#8c8c8c" }} />} placeholder="Registration No" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                label="Mobile"
                                name="supplierMobile"
                                rules={[{ pattern: /^[0-9+\-\s()]{7,15}$/, message: "Enter a valid mobile number" }]}
                            >
                                <Input prefix={<PhoneOutlined style={{ color: "#52c41a" }} />} placeholder="+94 7X XXX XXXX" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Landline" name="supplierLandno">
                                <Input prefix={<PhoneOutlined style={{ color: "#8c8c8c" }} />} placeholder="Landline number" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Email" name="supplierEmail" rules={[{ type: "email", message: "Not a valid email" }]}>
                                <Input prefix={<MailOutlined style={{ color: "#1677ff" }} />} placeholder="supplier@example.com" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Website" name="supplierWeb">
                                <Input prefix={<GlobalOutlined style={{ color: "#722ed1" }} />} placeholder="https://example.com" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Status" name="supplierIsActive" valuePropName="checked" initialValue={true}>
                                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Address" name="supplierAddress">
                                <TextArea rows={2} placeholder="Enter supplier address" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Note" name="supplierNote">
                                <TextArea rows={2} placeholder="Additional notes about this supplier" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row justify="end" style={{ marginTop: 8 }}>
                        <Col>
                            <Form.Item>
                                <Space>
                                    <Button danger onClick={() => { setIsAddModalVisible(false); addForm.resetFields(); }}>
                                        Cancel
                                    </Button>
                                    <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={loading}>
                                        Add Supplier
                                    </Button>
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>

            {/* // Footer section */}
            <Footerdiv />

            {/* // View Supplier Details Modal */}
            <Modal
                title={
                    <Space>
                        <EyeFilled style={{ color: "#1890ff", fontSize: 20 }} />
                        Supplier Details
                    </Space>
                }
                open={isViewModalVisible}
                onCancel={() => {
                    setIsViewModalVisible(false);
                    setViewSupplierData(null);
                }}
                footer={
                    <Button onClick={() => {
                        setIsViewModalVisible(false);
                        setViewSupplierData(null);
                    }}>
                        Close
                    </Button>
                }
                width={720}
                destroyOnClose
            >
                {viewSupplierData && (
                    <div style={{ padding: "8px 0" }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Supplier Name</Text>
                                    <Text strong style={{ fontSize: 15 }}>{viewSupplierData.supplier_name || "\u2014"}</Text>
                                </div>
                            </Col>
                            <Col xs={24} md={6}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Supplier Type</Text>
                                    <Tag color={viewSupplierData.supplier_type === "Individual" ? "cyan" : viewSupplierData.supplier_type === "Company" ? "blue" : viewSupplierData.supplier_type === "Partnership" ? "purple" : "default"}>
                                        {viewSupplierData.supplier_type || "\u2014"}
                                    </Tag>
                                </div>
                            </Col>
                            <Col xs={24} md={6}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Status</Text>
                                    {viewSupplierData.supplier_is_active == 1 ? (
                                        <Space size={4}>
                                            <CheckCircleFilled style={{ color: "#52c41a", fontSize: 16 }} />
                                            <Text style={{ color: "#52c41a" }}>Active</Text>
                                        </Space>
                                    ) : (
                                        <Space size={4}>
                                            <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 16 }} />
                                            <Text style={{ color: "#ff4d4f" }}>Inactive</Text>
                                        </Space>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        <Divider style={{ margin: "12px 0" }} />

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={8}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                                        <PhoneOutlined style={{ marginRight: 4, color: "#52c41a" }} />Mobile
                                    </Text>
                                    <Text>{viewSupplierData.supplier_mobile || "\u2014"}</Text>
                                </div>
                            </Col>
                            <Col xs={24} md={8}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                                        <PhoneOutlined style={{ marginRight: 4 }} />Landline
                                    </Text>
                                    <Text>{viewSupplierData.supplier_landno || "\u2014"}</Text>
                                </div>
                            </Col>
                            <Col xs={24} md={8}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                                        <MailOutlined style={{ marginRight: 4, color: "#1677ff" }} />Email
                                    </Text>
                                    <Text>{viewSupplierData.supplier_email || "\u2014"}</Text>
                                </div>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                            <Col xs={24} md={8}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                                        <GlobalOutlined style={{ marginRight: 4, color: "#722ed1" }} />Website
                                    </Text>
                                    {viewSupplierData.supplier_web ? (
                                        <a href={viewSupplierData.supplier_web.startsWith("http") ? viewSupplierData.supplier_web : `https://${viewSupplierData.supplier_web}`} target="_blank" rel="noreferrer">
                                            {viewSupplierData.supplier_web}
                                        </a>
                                    ) : (
                                        <Text>\u2014</Text>
                                    )}
                                </div>
                            </Col>
                            <Col xs={24} md={8}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                                        <IdcardOutlined style={{ marginRight: 4 }} />Reg. No
                                    </Text>
                                    <Text>{viewSupplierData.supplier_reg_no || "\u2014"}</Text>
                                </div>
                            </Col>
                            <Col xs={24} md={8}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                                        <EnvironmentOutlined style={{ marginRight: 4, color: "#fa8c16" }} />Address
                                    </Text>
                                    <Text>{viewSupplierData.supplier_address || "\u2014"}</Text>
                                </div>
                            </Col>
                        </Row>

                        <Row gutter={[16, 16]} style={{ marginTop: 8 }}>
                            <Col xs={24}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Notes</Text>
                                    <Text>{viewSupplierData.supplier_note || "\u2014"}</Text>
                                </div>
                            </Col>
                        </Row>

                        <Divider style={{ margin: "12px 0" }} />

                        <Row gutter={[16, 16]}>
                            <Col xs={24} md={12}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Last Updated</Text>
                                    <Text>{viewSupplierData.changed_dt ? dayjs(viewSupplierData.changed_dt).format("YYYY-MM-DD HH:mm") : "\u2014"}</Text>
                                </div>
                            </Col>
                            {/* <Col xs={24} md={12}>
                                <div className="fluid-view-field">
                                    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Updated By</Text>
                                    <Text>{viewSupplierData.changed_by || "\u2014"}</Text>
                                </div>
                            </Col> */}
                        </Row>
                    </div>
                )}
            </Modal>


            {/* // Edit Supplier Modal */}
            <Modal
                title={
                    <Space>
                        <EditTwoTone style={{ fontSize: 20 }} />
                        Edit Supplier Details
                    </Space>
                }
                open={isModalVisible}
                onCancel={() => {
                    setIsModalVisible(false);
                    form.resetFields();
                }}
                footer={null}
                width={720}
                destroyOnClose
            >
                <Form form={form} onFinish={handleUpdate} layout="vertical">
                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Supplier Name" name="supplierName" rules={[{ required: true, message: "Please enter supplier name" }]}>
                                <Input placeholder="Supplier name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="Supplier Type" name="supplierType" rules={[{ required: true, message: "Select type" }]}>
                                <Select allowClear placeholder="Select type">
                                    {SUPPLIER_TYPE_OPTIONS.map((opt) => (
                                        <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="Reg. No" name="supplierRegNo">
                                <Input placeholder="Registration No" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Mobile" name="supplierMobile" rules={[{ pattern: /^[0-9+\-\s()]{7,15}$/, message: "Enter a valid mobile number" }]}>
                                <Input prefix={<PhoneOutlined style={{ color: "#52c41a" }} />} placeholder="+94 7X XXX XXXX" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Landline" name="supplierLandno">
                                <Input prefix={<PhoneOutlined />} placeholder="Landline number" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Email" name="supplierEmail" rules={[{ type: "email", message: "Not a valid email" }]}>
                                <Input prefix={<MailOutlined style={{ color: "#1677ff" }} />} placeholder="supplier@example.com" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Website" name="supplierWeb">
                                <Input prefix={<GlobalOutlined style={{ color: "#722ed1" }} />} placeholder="https://example.com" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={6}>
                            <Form.Item label="Status" name="supplierIsActive" valuePropName="checked">
                                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[16, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item label="Address" name="supplierAddress">
                                <TextArea rows={2} placeholder="Supplier address" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Note" name="supplierNote">
                                <TextArea rows={2} placeholder="Additional notes" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row justify="end" style={{ marginTop: 12 }}>
                        <Form.Item>
                            <Space>
                                <Button danger onClick={() => { setIsModalVisible(false); form.resetFields(); }}>
                                    Cancel
                                </Button>
                                <Button type="primary" htmlType="submit" loading={loading}>
                                    Save Changes
                                </Button>
                            </Space>
                        </Form.Item>
                    </Row>
                </Form>
            </Modal>
        </Layout>
    );
};

export default ManageSupplier;